import { FC, useEffect, useMemo, useState } from "react"
import AppLayout from "../../components/layout/app-layout/app-layout";
import Tabs from "../../components/shared/Tabs";
import serviceSales, { Invoice } from "../../services/service-sales";
import DataTable from "../../components/shared/DataTable/DataTable";
import { autosave, formatDate } from "../../util";
import Field from "../../components/shared/Field/Field";
import { useTranslation } from "react-i18next";

type Props = {
  value: string,
  onBlur: (value: string) => void
}

const InvoiceNumberField : FC<Props> = ({value, onBlur}) => {
  const [inputValue, setInputValue] = useState(value);
  return (
    <Field
      value={inputValue}
      onChange={setInputValue}
      onBlur={() => onBlur(inputValue)}
    />
  );
}

const PageInvoices : FC = () => {
  const [t] = useTranslation('invoices');
  const [selectedTab, setSelectedTab] = useState(0);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoiceNumbers, setInvoiceNumbers] = useState<{[id: string]: string}>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const invoices = await serviceSales.fetchInvoices();
      setInvoices(invoices);
      const invoiceNumbers = invoices.reduce((obj: {[id: string]: string}, invoice: Invoice) => {
        obj[invoice._id] = invoice.invoiceNumber;
        return obj;
      }, {});
      setInvoiceNumbers(invoiceNumbers);
      setLoading(false);
    })();
  }, []);

  const columns = useMemo(() => [
    {
      title: t('table.header.product', 'Produkt'),
      field: "product",
    },
    {
      title: t('table.header.organization', 'Organisation'),
      field: "organization",
    },
    {
      title: t('table.header.invoiceEmail', 'Fakturaadress'),
      field: "invoiceEmail",
    },
    {
      title: t('table.header.invoiceRef', 'Fakturareferens'),
      field: "invoiceRef",
    },
    {
      title: t('table.header.startDate', 'Från'),
      field: "startDate",
      render: (rowData: Invoice) => formatDate(rowData.startDate),
    },
    {
      title: t('table.header.endDate', 'Till'),
      field: "endDate",
      render: (rowData: Invoice) => formatDate(rowData.endDate),
    },
    {
      title: t('table.header.amount', 'Belopp'),
      field: "amount",
    },
    {
      title: t('table.header.cause', 'Anledning'),
      field: "amount",
      render: (rowData: Invoice) => rowData.cause == "ORDER" ?
        t('table.row.cause.order', 'Beställning')
        : t('table.row.cause.renewed', 'Förnyad')
    },
    {
      title: t('table.header.invoiceNumber', 'Fakturanummer'),
      field: "invoiceNumber",
      render: (rowData: Invoice) => {
        const id = rowData._id;
        return (
          <InvoiceNumberField
            value={invoiceNumbers[id] ?? ""}
            onBlur={(value: string) => {
              setInvoiceNumbers({...invoiceNumbers, [id]: value});
              autosave(async () => {
                await serviceSales.saveInvoiceNumber(rowData._id, value);
              });
            }}
          />
        );
      }
    }
  ], [invoiceNumbers]);

  return (
    <AppLayout title={t('pageTitle', 'Fakturor')}>
      <div style={{margin: "32px"}}>
        <div className="tab-container">
          <Tabs
            tabs={[t('tabs.all', 'Alla')]}
            selected={selectedTab}
            onChange={tab => { setSelectedTab(tab); }}
          />
          <div className="tab-actions"></div>
        </div>
        <DataTable
          columns={columns}
          data={invoices}
          isLoading={loading}
        />
      </div>
    </AppLayout>
  );
}

export default PageInvoices;
